import { FC, useCallback, useEffect } from 'react';
import { Box } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Roles } from '@/_shared/constants/roles';
import { useDialog } from '@/_shared/hooks/useDialog';
import { ContentType } from '@/swagger/constants/contents-orchestrator-enums';
import { postContentEndCall, postContentProgressCall } from '@/training/hardSkills/state';
import { DialogConfirmExitFromDocument } from '../DialogConfirmExitFromDocument';
import { DialogFeedbackDocument } from '../DialogFeedbackDocument';
import { Player } from './Player';

type Props = {
  id: string;
  open: boolean;
  url: string;
  title?: string;
  startFrom?: number;
  onClose: () => void;
};

export const DocumentPlayer: FC<Props> = ({ id, open, url, title, startFrom, onClose }) => {
  const { openDialog: openDialogConfirmExit } = useDialog('confirmExitFromDocumentDialog');
  const { openDialog: openDialogFeedback } = useDialog('feedbackDocumentDialog');

  const { execute: executePostContentEnd } = postContentEndCall.hook();
  const { execute: executePostContentProgress } = postContentProgressCall.hook();

  const onCloseDocument = useCallback(() => {
    openDialogConfirmExit();
  }, [openDialogConfirmExit]);

  const onDocumentEndReading = useCallback(() => {
    executePostContentEnd({
      contentId: id,
      successSaga: () => {
        openDialogFeedback();
      },
    });
  }, [id, executePostContentEnd, openDialogFeedback]);

  const onDocumentProgress = useCallback(
    (activePage: number) => {
      if (activePage > 0) {
        executePostContentProgress({
          contentId: id,
          progress: {
            type: ContentType.PDF,
            page: activePage,
          },
        });
      }
    },
    [executePostContentProgress, id],
  );

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseDocument();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onCloseDocument]);

  if (!open) {
    return null;
  }

  return (
    <Box
      width='100%'
      height='100vh'
      style={{
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ClaimsComponent
        roles={[Roles.Admin, Roles.HR_Delegate]}
        containerProps={{
          width: '100%',
          height: '100%',
        }}
      >
        <Player
          url={url}
          title={title}
          startFrom={startFrom}
          onCloseDocument={onCloseDocument}
          onFinishReading={onClose}
        />
      </ClaimsComponent>
      <ClaimsComponent
        roles={[Roles.Employee, Roles.Manager]}
        containerProps={{
          width: '100%',
          height: '100%',
        }}
      >
        <>
          <Player
            url={url}
            title={title}
            startFrom={startFrom}
            onCloseDocument={onCloseDocument}
            onFinishReading={onDocumentEndReading}
            onProgress={onDocumentProgress}
          />
          <DialogFeedbackDocument id={id} onClose={onClose} />
        </>
      </ClaimsComponent>

      {/* Dialogs */}
      <DialogConfirmExitFromDocument onClose={onClose} />
    </Box>
  );
};
